import {
  ThemeProvider
} from '@mui/material/styles';
import {
  SnackbarProvider,
  useSnackbar
} from 'notistack';
import React from 'react';
import {
  createRoot
} from 'react-dom/client';
import {
  HashRouter
} from 'react-router-dom';
import './assets/scripts/global';
import './assets/styles/__imports.css';
import GlobalComponents from './components/Home/GlobalComponents';
import Theme from './components/Layout/Libraries/Theme.lib';
import Box from './components/Layout/Wrappers/Box';
import Button from './components/Layout/Wrappers/Button';
import {
  LoadingMessage
} from './components/Layout/Wrappers/Message';
import Loader from './components/Shared/Loader';
import './i18n/i18n.tool';
import {
  NOT
} from './lib/Boolean.lib';
import Core from './lib/Core';
import './lib/Debug.lib';
import {
  Fun
} from './lib/Function.lib';
import useStart from './lib/hooks/useStart.hook';
import {
  Obj
} from './lib/Object.lib';
import {
  trim
} from './lib/String.lib';
import {
  matchLocation,
  reloadLocation,
  setLocation
} from './lib/URL.lib';
import AdminUser from './routes/Admin.routes';
import CommonUser from './routes/CommonUser.routes';
import EmployerUser from './routes/Employer.routes';
import Error from './routes/Error';
import RecruiterOnDev from './routes/RecruiterOnDev.routes';
import Unlogged from './routes/Unlogged.routes';

export const KEY__CONTROLLER__APP = 'controller__app_with_snackbar';
export const getControllerNotistack = () => {
  return Obj(Core.getKeyValue(KEY__CONTROLLER__APP));
}
export const setControllerNotistack = (controller) => {
  return Core.setKeyValue(KEY__CONTROLLER__APP, controller);
}
export function enqueueNotification({
  message = '',
  severity = 'info',
  onClose,
  dismissLabel = 'Dismiss'
} = {}) {
  try {
    console.debug('enqueueNotification', { message, severity, onClose });
    const options = {
      variant: severity || 'info',
      persist: onClose ? true : false,
      action: (key) => (
        <Button flat small className='fw-700' onClick={() => {
          getControllerNotistack().closeSnackbar(key);
          Fun(onClose)();
        }}>
          {dismissLabel}
        </Button>
      )
    };
    if (NOT(options.persist)) {
      options.autoHideDuration = (severity !== 'info') ? 6000 : 3000;
    }
    getControllerNotistack().enqueueSnackbar(
      React.isValidElement(message)
        ? message
        : message?.toString() || trim(message) || <Loader>Loading...</Loader>,
      options
    );
  }
  catch (error) {
    console.error(error);
    Fun(onClose)();
  }
}

function App(props) {
  const { busy, error, ready } = useStart();
  setControllerNotistack(useSnackbar());
  setTimeout(() => {
    if (Core.isLoggedOut() && NOT(matchLocation(/login|reset|jwt/i))) {
      setLocation('/login');
      reloadLocation();
    }
  });
  /**
   * @note
   * The ThemeProvider and HashRouter
   * must wrap all rendered elements
   * in order to work properly.
   */
  return (

    <Box role='Main' className="vh-100 vw-100">
      <LoadingMessage show={busy}
        className='vh-100 vw-100 m-0 flex-align-center bg-cyan-lighter'
      >
        Starting...
      </LoadingMessage>
      <Error error={error} />
      <GlobalComponents />
      {ready &&
        <Box role='Content' className='vh-100 vw-100'>
          {
            Core.isLoggedOut() ? (
              <Unlogged />
            ) : Core.isAdmin() ? (
              <AdminUser />
            ) : Core.isEmployer() ? (
              <EmployerUser />
            ) : Core.isRecruiterOnDev() ? (
              <RecruiterOnDev />
            ) : (
              <CommonUser />
            )
          }
        </Box>
      }
    </Box>
  );
};

createRoot(document.getElementById('Root')).render(
  <ThemeProvider theme={Theme}>
    <HashRouter>
      <SnackbarProvider maxSnack={3}>
        <App />
      </SnackbarProvider>
    </HashRouter>
  </ThemeProvider>
);
